import classNames from 'classnames'
import { useFormikContext } from 'formik'
import {
  Box,
  Flex,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  Switch,
  FullBlockSpinner,
  InputWithState
} from 'front-end/src/components'
import {
  Byline,
  BylineSection
} from 'front-end/src/components/common/byline/Byline'
import { throttle } from 'lodash'
import React, { useRef } from 'react'
import { LooopS3BucketEnum } from 'types/UploadCredentials'
import { SimpleGrid } from '@chakra-ui/react'
import { t, Trans } from '@lingui/macro'
import { LooopS3FileUpload } from 'front-end/src/app/util/LooopS3FileUpload'
import EditorQuill from '../../../../../components/common/quill/EditorQuill'
import EventStatus, {
  StatusOfEventProps,
  StatusEventStatusType,
  getStatus
} from '../../../components/event-status/EventStatus'
import { FacilitatorSelector } from '../../../components/facilitator-selector/FacilitatorSelector'
import { EventDetailCalendarIntegration } from './EventDetailCalendarIntegration'
import { EventDetailRepeat } from './EventDetailRepeat'
import EventDetailTime from './EventDetailTime'
import { FormLabelIcon } from '../../../components/event-form/EventFormLabelIcon'
import IconLocationPin from 'front-end/src/components/common/icon/IconLocationPin'
import IconDescription from 'front-end/src/components/common/icon/IconDescription'
import { IconEyeglasses } from 'front-end/src/components/common/icon/IconEyeglasses'
import {
  EventSettingsBlock,
  EventSettingsDescription,
  EventSettingsHeader,
  EventSettingsRowContent
} from '../../../components/event-form/EventSettingsBlocks'
import IconChair from 'front-end/src/components/common/icon/IconChair'
import { TooltipHelpInfo } from 'front-end/src/components/common/tooltip/TooltipHelp'
import IconBubble from 'front-end/src/components/common/icon/IconBubble'
import IconTag from 'front-end/src/components/common/icon/IconTag'
import TagSelect, {
  TAG_TARGET_TYPES
} from 'front-end/src/components/common/tag/TagSelect'
import IconPeople from 'front-end/src/components/common/icon/IconPeople'
import Fieldset from 'front-end/src/components/common/form/fieldset/Fieldset'
import { RadioDescription } from 'front-end/src/components/common/form/radio/Radio'
import { ManageEventDetailDayjs } from '../../EventEditPage'
import { useManageEventContext } from '../../../ManageEventContext'
import { RecurringEventAttendanceTypeEnum } from 'front-end/src/graphql/private/PrivateSchemaTypes'
import { css } from '@emotion/react'
import { useFeatureFlags } from 'front-end/src/app/hooks/useFeatureFlags'
import { useGetCalendarIntegrationsQuery } from 'front-end/src/pages/manage-events/event-edit/EventEdit.codegen'
import { SyncGeneralAlert } from 'front-end/src/pages/manage-events/components/sync-general-alert/SyncGeneralAlert'

const DEFAULT_DEBOUNCE = 500

const EventDetail = () => {
  const { featureFlags } = useFeatureFlags()
  const { isRecurringSeries, event, isRecurringInstance } =
    useManageEventContext()
  const { setFieldValue, handleSubmit, handleChange, values } =
    useFormikContext<ManageEventDetailDayjs>()

  const { data: orgCalData, loading: isFetchingOrg } =
    useGetCalendarIntegrationsQuery({
      fetchPolicy: 'cache-first',
      skip: !featureFlags?.calendars_integration
    })

  const quill = useRef<EditorQuill>(null)

  const hasGoogleIntegration =
    !!orgCalData?.admin?.organisation?.calendars.google?.calendarId

  const hasMSIntegration =
    !!orgCalData?.admin?.organisation?.calendars.msgraph?.accountName

  const throttledUpdate = throttle(
    (content) => setFieldValue('description', content),
    500,
    { leading: false, trailing: true }
  )

  const showAllAttendance =
    !event.published_date ||
    (event.published_date &&
      values.attendance_type === RecurringEventAttendanceTypeEnum.all)

  const showMultipleAttendance =
    !event.published_date ||
    (event.published_date &&
      values.attendance_type === RecurringEventAttendanceTypeEnum.multiple)

  const eventStatus = getStatus(event as StatusOfEventProps)

  return (
    <>
      {isFetchingOrg ? (
        <FullBlockSpinner />
      ) : (
        <>
          <form
            onSubmit={handleSubmit}
            className={classNames('EventDetailTab')}
          >
            <SimpleGrid columns={1} spacing="48px">
              <SimpleGrid columns={1} spacing="8px">
                <FormControl id="title">
                  <FormLabel htmlFor="title" mt={0}>
                    <Trans>Event name</Trans>
                  </FormLabel>
                  <InputWithState
                    name="title"
                    type="text"
                    variant="filled"
                    size="lg"
                    onChange={handleChange}
                    defaultValue={values.title || ''}
                    debounce={DEFAULT_DEBOUNCE}
                    dataTest="EventNameInput"
                  />
                </FormControl>
                <Byline>
                  <BylineSection showDot={false}>
                    <Trans>Created by {event.author.display_name}</Trans>
                  </BylineSection>
                  <BylineSection showDot={false}>
                    <EventStatus
                      ml="6px"
                      as="span"
                      event={event as StatusOfEventProps}
                    />
                  </BylineSection>
                </Byline>
              </SimpleGrid>
              <Flex>
                <Box width="66%">
                  <EventDetailTime />

                  {values.start_time &&
                    !isRecurringInstance &&
                    (isRecurringSeries ||
                      (!isRecurringSeries && !event.published_date)) && (
                      <EventDetailRepeat key={event.published_date} />
                    )}

                  <FormControl as={Flex} mb="16px" flexGrow={1} id="location">
                    <Flex>
                      <FormLabelIcon
                        icon={<IconLocationPin aria-label="Location or URL" />}
                      />
                    </Flex>
                    <Flex flexGrow={1}>
                      <InputWithState
                        name="location"
                        type="text"
                        variant="filled"
                        placeholder="Location or URL"
                        onChange={handleChange}
                        value={values.location || ''}
                        debounce={DEFAULT_DEBOUNCE}
                        dataTest="LocationInput"
                      />
                    </Flex>
                  </FormControl>

                  <EventDetailCalendarIntegration
                    hasGoogleIntegration={hasGoogleIntegration}
                    hasMSIntegration={hasMSIntegration}
                  />

                  <Flex mb="16px">
                    <Flex>
                      <FormLabelIcon
                        icon={<IconDescription aria-label="Description" />}
                      />
                    </Flex>
                    <Flex flexGrow={1} overflow="hidden">
                      <FormControl>
                        <EditorQuill
                          ref={quill}
                          minHeight={300}
                          placeholder={t`Describe the event...`}
                          value={values.description || ''}
                          onChange={throttledUpdate}
                          uploadFn={async (fileName, blob) => {
                            return LooopS3FileUpload({
                              blob: blob,
                              S3UploadProps: {
                                filename: fileName,
                                S3path: `events/event-${event?.id}`,
                                bucket: LooopS3BucketEnum.PUBLIC
                              }
                            })
                          }}
                          dataTest="DescriptionInput"
                        />
                      </FormControl>
                    </Flex>
                  </Flex>
                </Box>
                <Box pl="48px" w="34%">
                  {(!!event?.recurrence?.freq ||
                    !!values.recurringEvent?.recurrence?.freq) &&
                    !isRecurringInstance && (
                      <EventSettingsBlock>
                        <EventSettingsHeader
                          icon={<IconPeople />}
                          titleText={t`Type of attendance`}
                          afterTitle={
                            <TooltipHelpInfo>
                              <Trans>
                                Type of attendance cannot be modified after
                                publishing
                              </Trans>
                            </TooltipHelpInfo>
                          }
                        />
                        <EventSettingsRowContent>
                          <Fieldset>
                            <RadioGroup
                              onChange={(value) =>
                                setFieldValue('attendance_type', value)
                              }
                              defaultValue={
                                (values.attendance_type as string) ||
                                RecurringEventAttendanceTypeEnum.all
                              }
                              css={css`
                                .chakra-radio__control[data-checked],
                                .chakra-radio__label[data-checked] {
                                  opacity: 1;
                                }
                              `}
                              data-test="AttendanceTypeSection"
                            >
                              {showAllAttendance && (
                                <Radio
                                  value={RecurringEventAttendanceTypeEnum.all}
                                  mb="24px"
                                  isDisabled={!!event.published_date}
                                  tooltipText={
                                    !!event.published_date
                                      ? t`You're unable to change this after publishing`
                                      : undefined
                                  }
                                  dataTest="AttendMultipleRadioButton"
                                >
                                  <Trans>Attend multiple occurrences</Trans>
                                  <RadioDescription>
                                    <Trans>
                                      Invited users can attend any or all
                                      occurrences in the event series. Can be
                                      used for events that are optional or where
                                      all events are required.
                                    </Trans>
                                  </RadioDescription>
                                </Radio>
                              )}
                              {showMultipleAttendance && (
                                <Radio
                                  value={
                                    RecurringEventAttendanceTypeEnum.multiple
                                  }
                                  isDisabled={!!event.published_date}
                                  tooltipText={
                                    !!event.published_date
                                      ? t`You're unable to change this after publishing`
                                      : undefined
                                  }
                                  dataTest="AttendSingleRadioButton"
                                >
                                  <Trans>Attend single occurrence</Trans>
                                  <RadioDescription>
                                    <Trans>
                                      Invited users can attend one occurrence in
                                      the event series. Can be used for repeated
                                      events where attendance is required for
                                      one event only.
                                    </Trans>
                                  </RadioDescription>
                                </Radio>
                              )}
                            </RadioGroup>
                          </Fieldset>
                        </EventSettingsRowContent>
                      </EventSettingsBlock>
                    )}
                  <EventSettingsBlock>
                    <EventSettingsHeader
                      icon={<IconChair />}
                      titleText={t`Places available`}
                      afterTitle={
                        <TooltipHelpInfo>
                          <Trans>
                            Number of places available for attendees. Leave
                            blank for unlimited.
                          </Trans>
                        </TooltipHelpInfo>
                      }
                    >
                      <InputWithState
                        type="number"
                        width="60px"
                        min={0}
                        value={values.participants_limit || ''}
                        variant="filled"
                        debounce={DEFAULT_DEBOUNCE}
                        onChange={(e) =>
                          setFieldValue(
                            'participants_limit',
                            Number(e.target.value)
                          )
                        }
                        dataTest="PlacesInput"
                      />
                    </EventSettingsHeader>
                  </EventSettingsBlock>

                  <EventSettingsBlock>
                    <EventSettingsHeader
                      icon={<IconBubble />}
                      titleText={t`Allow comments`}
                    >
                      <Switch
                        isChecked={values.allow_comments}
                        onChange={(e) =>
                          setFieldValue('allow_comments', e.target.checked)
                        }
                        dataTest="AllowCommentsSwitch"
                      />
                    </EventSettingsHeader>
                  </EventSettingsBlock>

                  <EventSettingsBlock>
                    <EventSettingsHeader
                      icon={<IconEyeglasses />}
                      titleText={t`Event facilitator`}
                    />
                    <EventSettingsRowContent dataTest="FacilitatorInput">
                      <FacilitatorSelector
                        value={values.facilitator}
                        onChange={(data: any) => {
                          setFieldValue('facilitator', data)
                          setFieldValue('facilitator_id', data?.id || null)
                        }}
                      />
                    </EventSettingsRowContent>
                    <EventSettingsDescription
                      text={t`Event facilitator will be added to event invitees and shown on event detail page.`}
                    />
                  </EventSettingsBlock>

                  <EventSettingsBlock>
                    <EventSettingsHeader
                      icon={<IconTag />}
                      titleText={t`Tags`}
                    />
                    <EventSettingsRowContent>
                      <TagSelect
                        target={{
                          type: isRecurringSeries
                            ? TAG_TARGET_TYPES.recurringEvent
                            : TAG_TARGET_TYPES.event,
                          id: event.id
                        }}
                        onChange={(data) => {
                          setFieldValue('tags', data)
                        }}
                        defaultValue={values.tags}
                        autoSave={false}
                      />
                    </EventSettingsRowContent>
                  </EventSettingsBlock>

                  <EventSettingsBlock>
                    <EventSettingsRowContent>
                      {!isRecurringSeries &&
                        eventStatus === StatusEventStatusType.OPEN && (
                          <SyncGeneralAlert
                            eventId={event.id}
                            isPublished={!!event.published_date}
                          />
                        )}
                    </EventSettingsRowContent>
                  </EventSettingsBlock>
                </Box>
              </Flex>
            </SimpleGrid>
          </form>
        </>
      )}
    </>
  )
}

export default EventDetail
